import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { SupplierPaymentCreateRequest } from '@/api/handlers/resources/suppliers/requests';
import type { SupplierPaymentWriteResponse } from '@/api/handlers/resources/suppliers/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { SupplierQueryKeys } from './supplierQueryKeys';

export type CreateSupplierPaymentVariables = {
    supplierId: string;
    body: SupplierPaymentCreateRequest;
};

/**
 * Records a payment against a supplier (BOS-120). Invalidates every supplier
 * query rather than just the payment list: the payment moves the supplier's
 * balance, so the management table and the statement are stale the moment it
 * lands.
 */
export const useCreateSupplierPaymentMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<SupplierPaymentWriteResponse>,
        Error,
        CreateSupplierPaymentVariables
    >({
        mutationFn: ({ supplierId, body }) =>
            apiClient.suppliers.createSupplierPayment(supplierId, body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: SupplierQueryKeys.all,
                });
            }
        },
    });
};
